import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PencilSimple } from "@phosphor-icons/react";
import { updatePersonalMemory } from "src/store/slices/profileMemorySlice";
import { trackAnalytics } from "src/pages/helpers";
import { EVENT_NAMES, EVENT_PARAMETERS, MODAL_NAMES, BUTTON_TYPES, BUTTON_ROLES, getPageName } from "src/utils/EventConstants";

const FIELDS = [
  { label: "Name", key: "name" },
  { label: "Location", key: "location" },
  { label: "Notes", key: "context", multiline: true },
];

const PersonalSection = () => {
  const dispatch = useDispatch();
  const personal = useSelector((state) => state.profileMemory.personal);
  const userMetadata = useSelector((state) => state.onboarding.userMetadata);
  const saving = useSelector((state) => state.profileMemory.updateStatus === "loading");

  const clerkName = [userMetadata?.first_name, userMetadata?.last_name].filter(Boolean).join(" ") || null;
  const displayName = personal.name || clerkName;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({ name: "", location: "", context: "" });

  useEffect(() => {
    setDraft({
      name: personal.name || clerkName || "",
      location: personal.location || "",
      context: personal.context || "",
    });
  }, [personal, clerkName]);

  const handleSave = () => {
    const changes = {};
    if (draft.name.trim() !== (personal.name || "")) changes.name = draft.name.trim();
    if (draft.location.trim() !== (personal.location || "")) changes.location = draft.location.trim();
    if (draft.context.trim() !== (personal.context || "")) changes.context = draft.context.trim();
    if (Object.keys(changes).length > 0) {
      dispatch(updatePersonalMemory(changes));
    }
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft({
      name: personal.name || clerkName || "",
      location: personal.location || "",
      context: personal.context || "",
    });
    setEditing(false);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Section header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-[3px] h-[18px] bg-[#1B2B48] rounded-full" />
          <h3 className="text-[13px] font-bold text-[#1B2B48] uppercase tracking-[0.05em]">About You</h3>
        </div>
        {!editing && (
          <button
            onClick={() => {
              trackAnalytics(
                EVENT_NAMES.CLICKED_BUTTON,
                {
                  [EVENT_PARAMETERS.PAGE_NAME]: getPageName(),
                  [EVENT_PARAMETERS.USER_ID]: userMetadata?.id,
                  [EVENT_PARAMETERS.CURRENT_PLAN_TIER]: userMetadata?.subscription_type || "anonymous",
                  [EVENT_PARAMETERS.MODAL_NAME]: MODAL_NAMES.MEMORY_SHELF,
                  [EVENT_PARAMETERS.BUTTON_TYPE]: BUTTON_TYPES.BUTTON_WITH_ICON,
                  [EVENT_PARAMETERS.BUTTON_TEXT]: "edit",
                  [EVENT_PARAMETERS.BUTTON_ROLE]: BUTTON_ROLES.EDIT,
                  [EVENT_PARAMETERS.MEMORY_SECTION]: "about_you",
                },
                userMetadata,
              );
              setDraft({
                name: personal.name || clerkName || "",
                location: personal.location || "",
                context: personal.context || "",
              });
              setEditing(true);
            }}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#6B7280] hover:text-[#1B2B48] cursor-pointer transition-colors"
          >
            <PencilSimple size={13} />
            Edit
          </button>
        )}
      </div>

      {/* Card */}
      {editing ? (
        <div className="border border-[#E4E4E4] rounded-xl bg-white p-3 flex flex-col gap-3">
          {FIELDS.map((field) => (
            <div key={field.key} className="flex flex-col gap-1.5">
              <label className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-[0.08em]">
                {field.label}
              </label>
              {field.multiline ? (
                <textarea
                  value={draft[field.key]}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  className="w-full text-[14px] text-[#1B2B48] bg-white border border-[#E4E4E4] rounded-[6px] px-2 py-2 resize-none focus:outline-none focus:ring-1 focus:ring-[#18475A]"
                  rows={3}
                />
              ) : (
                <input
                  type="text"
                  value={field.key === "name" ? displayName : draft[field.key]}
                  onChange={(e) => setDraft({ ...draft, [field.key]: e.target.value })}
                  disabled={field.key === "name"}
                  className={`w-full text-[14px] text-[#1B2B48] border border-[#E4E4E4] rounded-[6px] px-2 py-2 focus:outline-none focus:ring-1 focus:ring-[#18475A] ${field.key === "name" ? "bg-[#F3F4F6] cursor-not-allowed opacity-60" : "bg-white"}`}
                />
              )}
            </div>
          ))}
          <div className="flex justify-end items-center gap-4 pt-2">
            <button
              onClick={handleCancel}
              className="text-[14px] font-medium text-[#6B7280] hover:text-[#1B2B48] cursor-pointer transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="text-[14px] font-medium text-white bg-[#1B2B48] px-6 py-2 rounded-full hover:bg-[#0d1524] transition-colors cursor-pointer disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </div>
      ) : (
        <div className="border border-[#E4E4E4] rounded-[6px] overflow-hidden py-1">
          {FIELDS.filter((field) => (field.key === "name" ? displayName : personal[field.key])).map((field) => (
            <div key={field.key} className={`flex items-start px-4 py-2.5 ${field.multiline ? 'flex-col' : 'flex-row'}`}>
              <span className="text-[13px] text-[#6B7280] w-[120px] shrink-0">{field.label}</span>
              <span className={`text-[13px] text-[#1B2B48] flex-1 break-words${field.multiline ? " whitespace-pre-wrap" : ""}`}>
                {field.key === "name" ? displayName : personal[field.key]}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PersonalSection;
